import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { getConsent, grantAnalyticsConsent, denyAnalyticsConsent } from '../lib/analytics'
import './CookieConsent.css'

/**
 * Einwilligungs-Banner fuer die Reichweitenmessung (Google Analytics 4).
 *
 * Seit der Umstellung auf Google Consent Mode v2 (lib/analytics.ts) entscheidet
 * das Banner nicht mehr darüber, OB gtag lädt, sondern nur noch darüber, ob
 * es etwas auf dem Gerät speichern darf. Ohne Zustimmung laufen cookielose,
 * nicht personenbezogene Treffer — mit Zustimmung kommen Cookies und
 * Wiedererkennung dazu.
 *
 * Ablehnen ist genauso einfach wie Zustimmen (gleichwertige Schaltflächen,
 * keine vorausgewählte Zustimmung). Ein Banner, das das Ablehnen versteckt,
 * erzeugt keine wirksame Einwilligung.
 *
 * Das Banner erscheint nur, solange keine Entscheidung vorliegt. Widerrufen
 * laesst sie sich in den Einstellungen (revokeAnalyticsConsent).
 */
export function CookieConsent() {
  const { t } = useTranslation('legal')
  // Sichtbarkeit beim ersten Rendern bestimmen statt in einem Effekt: ein
  // setState im Effekt loest eine zweite Renderrunde aus (und die Lint-Regel
  // gegen Kaskaden-Renders), und das Banner wuerde sichtbar aufblitzen, obwohl
  // die Antwort laengst im Speicher steht.
  const [visible, setVisible] = useState(() => getConsent() === 'unknown')

  // Kein Effekt mehr für wiederkehrende Besucher: initAnalytics() in main.tsx
  // liest den gespeicherten Status selbst und setzt ihn direkt als
  // `consent default`, noch bevor React überhaupt rendert.

  if (!visible) return null

  return (
    <div className="cookie-consent" role="dialog" aria-live="polite"
         aria-label={t('consent.title', 'Reichweitenmessung')}
         data-testid="cookie-consent">
      <div className="cookie-consent__inner">
        <div className="cookie-consent__text">
          <strong>{t('consent.title', 'Reichweitenmessung')}</strong>
          <p>
            {t(
              'consent.body',
              'Wir messen mit Google Analytics, wie unsere Seiten genutzt werden. Das hilft uns, sie zu verbessern. Ohne deine Zustimmung zählen wir nur anonym mit — ohne Cookies und ohne dich wiederzuerkennen.',
            )}{' '}
            <Link to="/privacy">{t('consent.privacyLink', 'Datenschutzerklärung')}</Link>
          </p>
        </div>
        <div className="cookie-consent__actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              denyAnalyticsConsent()
              setVisible(false)
            }}
          >
            {t('consent.deny', 'Ablehnen')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              grantAnalyticsConsent()
              setVisible(false)
            }}
          >
            {t('consent.grant', 'Zustimmen')}
          </button>
        </div>
      </div>
    </div>
  )
}
